import React from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useMutation, useQuery } from 'react-apollo'

import routes from 'src/lib/rails-routes/rails-routes'
import LearningLanguageLevelAssessmentPlaceholder from 'src/pages/LearningLanguageLevelAssessment/LearningLanguageLevelAssessmentPlaceholder'
import LearningLanguageLevelAssessment from 'src/pages/LearningLanguageLevelAssessment/LearningLanguageLevelAssessment'
import DataFetchError from 'src/ui/DataFetchError/DataFetchError'
import { t } from 'src/lib/i18n-react'
import { postMessageToWebView } from 'lib/mobileHelper'

const UPDATE_CEFR_LEVEL = gql`
  mutation UPDATE_CEFR_LEVEL(
    $languageCode: LanguageEnum!
    $cefrLevel: CefrLevelCode!
    $startPosition: LevelStartPosition
  ) {
    response: updateUserLearningLanguage(
      languageCode: $languageCode
      cefrLevel: $cefrLevel
      startPosition: $startPosition
    ) {
      userLanguage {
        progressType
      }
      errors {
        code
        message
      }
    }
  }
`

const ASSESSMENT_LEVELS = gql`
  query CURRICULUM($l1Code: LanguageEnum!, $l2Code: LanguageEnum!) {
    curriculum(native: $l1Code, learning: $l2Code) {
      cefrLevels {
        id
        code
        label
        description
        startingDescription
        midpointDescription
      }
    }
    currentUser {
      id
      progress(code: $l2Code) {
        cefrLevel {
          id
          code
          label
        }
        cefrLevelProgress
      }
      isNew
    }
  }
`

const LevelAssessmentLazy = ({
  l2Code,
  onShowSnackbar,
  l1Code,
  displayPlacementTest,
  mobileAppToken,
  isSignup,
}) => {
  const [
    updateUserLanguageCefrLevel,
    { loading: updateUserLanguageCefrLevelLoading },
  ] = useMutation(UPDATE_CEFR_LEVEL)

  const response = useQuery(ASSESSMENT_LEVELS, {
    variables: {
      l1Code,
      l2Code,
    },
  })

  const { data, loading, refetch } = response

  const currentUserLevel = () => {
    return data.currentUser?.progress
  }
  const newUser = data?.currentUser.isNew

  const selfAssessmentLevels = () => {
    const levels = data?.curriculum?.cefrLevels

    function reducer(accumulator, currentValue, currentIndex, sourceArray) {
      const mainLevel = {
        code: currentValue.code,
        label: currentValue.label,
        description: currentValue.startingDescription,
        start: 'start',
      }
      const midLevel = {
        code: currentValue.code,
        label: currentValue.label,
        description: currentValue.midpointDescription,
        start: 'middle',
      }

      if (currentIndex === sourceArray.length - 1) {
        return [...accumulator, mainLevel]
      } else {
        return [...accumulator, mainLevel, midLevel]
      }
    }

    return levels.reduce(reducer, [])
  }

  const userLevel = () => {
    const userLevel = currentUserLevel()
    let startpoint
    if (userLevel.cefrLevelProgress >= 0.5) {
      startpoint = 'middle'
    } else if (userLevel.cefrLevelProgress < 0.5) {
      startpoint = 'start'
    }

    return {
      code: userLevel.cefrLevel.code,
      label: userLevel.cefrLevel.label,
      start: startpoint,
    }
  }

  const handleSaveLanguageLevel = async (level) => {
    if (updateUserLanguageCefrLevelLoading) {
      return
    }

    const errorMessage = t(
      'Error while saving level. Please refresh and try again later.'
    )

    try {
      const options = {
        variables: {
          languageCode: l2Code,
          cefrLevel: level.code,
          startPosition: level.start,
        },
      }

      const mutationResponse = await updateUserLanguageCefrLevel(options)
      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        if (mobileAppToken) {
          postMessageToWebView('token', mobileAppToken)
        } else {
          if (isSignup) {
            window.location = routes.survey({ code: l2Code })
          } else {
            window.location = routes.home_student({ code: l2Code })
          }
        }
      }
    } catch (error) {
      console.log(error)
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  if (loading) {
    return (
      <LearningLanguageLevelAssessmentPlaceholder
        displayPlacementTest={displayPlacementTest}
      />
    )
  }

  if (data) {
    return (
      <LearningLanguageLevelAssessment
        currentLevel={userLevel()}
        languageCode={l2Code}
        levels={selfAssessmentLevels()}
        onSubmitLevel={handleSaveLanguageLevel}
        displayPlacementTest={displayPlacementTest}
        isSignup={isSignup}
      />
    )
  }

  return <DataFetchError graphQLResponse={{ response }} />
}

LevelAssessmentLazy.propTypes = {
  onShowSnackbar: PropTypes.func,
  l2Code: PropTypes.string,
  l1Code: PropTypes.string,
  displayPlacementTest: PropTypes.bool,
  mobileAppToken: PropTypes.string,
  isSignup: PropTypes.bool,
}

export default LevelAssessmentLazy
